export const MAX_HP = 8;
export const FINAL_DEPTH = 4;

export const TOOLS = {
  mirror: {
    name: 'Mirror Shard',
    description: 'Reverse the digits of your roll.',
  },
  chisel: {
    name: 'Fate Chisel',
    description: 'Reroll either digit of your roll.',
  },
  swap: {
    name: "Thief's Glove",
    description: 'Swap your roll with the enemy roll.',
  },
  memory: {
    name: 'Memory Rune',
    description:
      'Store a roll and replace it. Recall the stored roll in a later clash.',
  },
};

export const DUNGEON = [
  [
    {
      id: 'gutter-imp',
      name: 'Gutter Imp',
      title: 'The Ash Passage',
      hp: 2,
      damage: 1,
      reward: { tool: 'mirror', amount: 1 },
      description:
        'A quick little monster guarding a splinter of polished glass.',
    },
    {
      id: 'coin-mimic',
      name: 'Coin Mimic',
      title: 'The Hungry Treasury',
      hp: 2,
      damage: 1,
      reward: { tool: 'swap', amount: 1 },
      description:
        'A treasure chest with sharp teeth and a useful stolen glove.',
    },
  ],
  [
    {
      id: 'glass-wraith',
      name: 'Glass Wraith',
      title: 'The Shattered Chapel',
      hp: 3,
      damage: 2,
      reward: { tool: 'chisel', amount: 1 },
      description:
        'It cuts deeply, but the tool in its chest can reshape a digit.',
    },
    {
      id: 'archive-spider',
      name: 'Archive Spider',
      title: 'The Webbed Archive',
      hp: 3,
      damage: 1,
      reward: { tool: 'memory', amount: 1 },
      description:
        'It remembers every trespasser. Its rune can remember a roll.',
    },
  ],
  [
    {
      id: 'rust-colossus',
      name: 'Rust Colossus',
      title: 'The Iron Crypt',
      hp: 4,
      damage: 2,
      reward: { tool: 'mirror', amount: 2 },
      description: 'A long fight for two shards and a safer path to the heart.',
    },
    {
      id: 'hex-hound',
      name: 'Hex Hound',
      title: 'The Crooked Kennel',
      hp: 3,
      damage: 2,
      reward: { tool: 'chisel', amount: 2 },
      description:
        'Fewer wounds to inflict, with two chances to carve fate afterward.',
    },
  ],
  [
    {
      id: 'dungeon-heart',
      name: 'The Dungeon Heart',
      title: 'The Final Chamber',
      hp: 5,
      damage: 2,
      reward: null,
      description: 'The dungeon itself rolls against you. Break it to escape.',
    },
  ],
];

export const createGame = (progress = {}) => ({
  phase: 'intro',
  depth: 1,
  hp: MAX_HP,
  roomId: null,
  enemyHp: 0,
  tools: { mirror: 1, chisel: 1, swap: 1, memory: 0 },
  storedRoll: null,
  playerRoll: null,
  enemyRoll: null,
  manipulationUsed: false,
  lastManipulation: null,
  clashVersion: 0,
  result: null,
  clearedRooms: [],
  history: [],
  bestDepth: progress.bestDepth || 0,
  victories: progress.victories || 0,
});

export const getRooms = (game) => DUNGEON[game.depth - 1] || [];

export const getRoom = (game) =>
  getRooms(game).find((room) => room.id === game.roomId) || null;

export const startRun = (game) => ({ ...createGame(game), phase: 'map' });

export const enterRoom = (game, roomId) => {
  if (game.phase !== 'map') return game;
  const room = getRooms(game).find((candidate) => candidate.id === roomId);
  if (!room) return game;
  return {
    ...game,
    phase: 'ready',
    roomId,
    enemyHp: room.hp,
    result: null,
  };
};

const validRoll = (roll) => Number.isInteger(roll) && roll >= 0 && roll <= 99;
const validDigit = (digit) =>
  Number.isInteger(digit) && digit >= 0 && digit <= 9;

export const rollClash = (game, playerRoll, enemyRoll) => {
  if (game.phase !== 'ready' || !validRoll(playerRoll) || !validRoll(enemyRoll))
    return game;
  return {
    ...game,
    phase: 'rolled',
    playerRoll,
    enemyRoll,
    manipulationUsed: false,
    lastManipulation: null,
    clashVersion: game.clashVersion + 1,
  };
};

const reverseRoll = (roll) =>
  Number(String(roll).padStart(2, '0').split('').reverse().join(''));

const canManipulate = (game, tool) =>
  game.phase === 'rolled' && !game.manipulationUsed && game.tools[tool] > 0;

export const applyMirror = (game) => {
  if (!canManipulate(game, 'mirror')) return game;
  return {
    ...game,
    playerRoll: reverseRoll(game.playerRoll),
    tools: { ...game.tools, mirror: game.tools.mirror - 1 },
    manipulationUsed: true,
    lastManipulation: 'mirror',
  };
};

export const applyChisel = (game, index, digit) => {
  if (
    !canManipulate(game, 'chisel') ||
    ![0, 1].includes(index) ||
    !validDigit(digit)
  )
    return game;
  const digits = String(game.playerRoll).padStart(2, '0').split('');
  digits[index] = String(digit);
  return {
    ...game,
    playerRoll: Number(digits.join('')),
    tools: { ...game.tools, chisel: game.tools.chisel - 1 },
    manipulationUsed: true,
    lastManipulation: 'chisel',
  };
};

export const applySwap = (game) => {
  if (!canManipulate(game, 'swap')) return game;
  return {
    ...game,
    playerRoll: game.enemyRoll,
    enemyRoll: game.playerRoll,
    tools: { ...game.tools, swap: game.tools.swap - 1 },
    manipulationUsed: true,
    lastManipulation: 'swap',
  };
};

export const applyMemory = (game, replacementRoll) => {
  if (game.phase !== 'rolled' || game.manipulationUsed) return game;
  if (game.storedRoll !== null) {
    return {
      ...game,
      playerRoll: game.storedRoll,
      storedRoll: null,
      manipulationUsed: true,
      lastManipulation: 'recall',
    };
  }
  if (game.tools.memory < 1 || !validRoll(replacementRoll)) return game;
  return {
    ...game,
    storedRoll: game.playerRoll,
    playerRoll: replacementRoll,
    tools: { ...game.tools, memory: game.tools.memory - 1 },
    manipulationUsed: true,
    lastManipulation: 'memory',
  };
};

const isPair = (roll) => {
  const digits = String(roll).padStart(2, '0');
  return digits[0] === digits[1];
};

export const getClashPreview = (game) => {
  if (game.phase !== 'rolled') return null;
  if (game.playerRoll > game.enemyRoll) {
    return {
      outcome: 'strike',
      damageToEnemy: isPair(game.playerRoll) ? 2 : 1,
      damageToPlayer: 0,
    };
  }
  if (game.playerRoll < game.enemyRoll) {
    const room = getRoom(game);
    return {
      outcome: 'hit',
      damageToEnemy: 0,
      damageToPlayer: room.damage + (isPair(game.enemyRoll) ? 1 : 0),
    };
  }
  return { outcome: 'clash', damageToEnemy: 1, damageToPlayer: 1 };
};

export const resolveClash = (game) => {
  const preview = getClashPreview(game);
  const room = getRoom(game);
  if (!preview || !room) return game;

  const hp = Math.max(0, game.hp - preview.damageToPlayer);
  const enemyHp = Math.max(0, game.enemyHp - preview.damageToEnemy);
  const roomCleared = enemyHp === 0;
  const won = roomCleared && game.depth === FINAL_DEPTH;
  const lost = hp === 0;
  const reward = roomCleared && room.reward ? room.reward : null;
  const tools = reward
    ? { ...game.tools, [reward.tool]: game.tools[reward.tool] + reward.amount }
    : game.tools;
  const result = {
    ...preview,
    playerRoll: game.playerRoll,
    enemyRoll: game.enemyRoll,
    roomName: room.name,
    roomCleared,
    reward,
  };
  const clearedRooms = roomCleared
    ? [...game.clearedRooms, room.id]
    : game.clearedRooms;
  const bestDepth =
    won || lost
      ? Math.max(game.bestDepth, won ? FINAL_DEPTH : game.depth)
      : game.bestDepth;

  return {
    ...game,
    phase: lost ? 'lost' : won ? 'won' : roomCleared ? 'roomClear' : 'result',
    hp,
    enemyHp,
    tools,
    result,
    clearedRooms,
    history: [result, ...game.history].slice(0, 8),
    bestDepth,
    victories: game.victories + (won ? 1 : 0),
  };
};

export const continueBattle = (game) => {
  if (game.phase !== 'result') return game;
  return {
    ...game,
    phase: 'ready',
    playerRoll: null,
    enemyRoll: null,
    result: null,
  };
};

export const continueDungeon = (game) => {
  if (game.phase !== 'roomClear' || game.depth >= FINAL_DEPTH) return game;
  return {
    ...game,
    phase: 'map',
    depth: game.depth + 1,
    roomId: null,
    enemyHp: 0,
    playerRoll: null,
    enemyRoll: null,
    result: null,
  };
};
